/**
 * Supabase subscription checker - Firebase sistemine paralel çalışır
 * Bu dosya sadece subscription durumunu kontrol eder, auth işlemleri Firebase'de kalır
 */

import { supabase } from '../lib/supabase/client';

export interface SupabaseSubscriptionData {
  subscription_id: string;
  subscription_status: string;
  price_id: string | null;
  product_id: string | null;
  customer_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Email ile customer ID'yi alır
 */
export async function getCustomerIdByEmail(email: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('customers')
      .select('customer_id')
      .eq('email', email)
      .single();

    if (error) {
      console.error('Error fetching customer ID:', error);
      return null;
    }

    return data?.customer_id || null;
  } catch (error) {
    console.error('Error in getCustomerIdByEmail:', error);
    return null;
  }
}

/**
 * Customer ID ile aktif subscription'ı kontrol eder
 */
export async function getActiveSubscriptionByCustomerId(customerId: string): Promise<SupabaseSubscriptionData | null> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('customer_id', customerId)
      .in('subscription_status', ['active', 'trialing'])
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      console.error('Error fetching active subscription:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getActiveSubscriptionByCustomerId:', error);
    return null;
  }
}

/**
 * Email ile aktif subscription durumunu kontrol eder
 */
export async function hasActiveSubscriptionByEmail(email: string): Promise<boolean> {
  try {
    // First try getting customer ID
    const customerId = await getCustomerIdByEmail(email);

    if (customerId) {
      const subscription = await getActiveSubscriptionByCustomerId(customerId);
      if (subscription) return true;
    }

    // Fallback: check subscriptions table directly by customer_email (for cases where customer record missed)
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('customer_email', email)
      .in('subscription_status', ['active', 'trialing'])
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (!error && data) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking active subscription by email:', error);
    return false;
  }
}

/**
 * Subscription durumunu string olarak döndürür
 */
export async function getSubscriptionStatusByEmail(email: string): Promise<'active' | 'inactive' | 'cancelled' | 'past_due' | 'paused'> {
  try {
    let status = 'inactive';

    // First try by customerId
    const customerId = await getCustomerIdByEmail(email);
    if (customerId) {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('subscription_status')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        status = data.subscription_status;
      }
    }

    // Fallback: check subscriptions table directly by customer_email
    if (status === 'inactive') {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('subscription_status')
        .eq('customer_email', email)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        status = data.subscription_status;
      }
    }

    if (status === 'inactive') return 'inactive';

    // Map Paddle statuses to our app statuses
    switch (status) {
      case 'active':
      case 'trialing':
        return 'active';
      case 'canceled':
        return 'cancelled';
      case 'past_due':
        return 'past_due';
      case 'paused':
        return 'paused';
      default:
        return 'inactive';
    }
  } catch (error) {
    console.error('Error getting subscription status by email:', error);
    return 'inactive';
  }
}
